import { Outlet, NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  TrendingUp, 
  TrendingDown, 
  Users, 
  UsersRound, 
  Calendar, 
  ClipboardList,
  LogOut,
  Menu,
  Trophy,
  CalendarDays,
  ClipboardCheck,
  History
} from 'lucide-react';
import { useState } from 'react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['auditor', 'supervisor', 'vendedor'] },
  { path: '/mi-historial', label: 'Mi Historial', icon: History, roles: ['vendedor'] },
  { path: '/ranking', label: 'Ranking', icon: Trophy, roles: ['auditor', 'supervisor'] },
  { path: '/analisis', label: 'Analisis', icon: TrendingUp, roles: ['auditor', 'supervisor'] },
  { path: '/asistencia', label: 'Horas Laborales', icon: ClipboardCheck, roles: ['auditor', 'supervisor'] },
  { path: '/ventas-historicas', label: 'Ventas Diarias', icon: ShoppingCart, roles: ['auditor'] },
  { path: '/diferencia-fonasa', label: 'Diferencia Fonasa', icon: TrendingDown, roles: ['auditor', 'supervisor', 'vendedor'] },
  { path: '/fonasa-registro', label: 'Fonasa', icon: ClipboardList, roles: ['auditor', 'supervisor', 'vendedor'] },
  { path: '/vendedores', label: 'Vendedores', icon: Users, roles: ['auditor', 'supervisor'] },
  { path: '/licencias', label: 'Licencias', icon: CalendarDays, roles: ['auditor', 'supervisor', 'vendedor'] },
  { path: '/grupos', label: 'Grupos', icon: UsersRound, roles: ['auditor', 'supervisor'] },
  { path: '/guardias', label: 'Guardias', icon: Calendar, roles: ['auditor', 'supervisor', 'vendedor'] },
  { path: '/fonasa-listado-more', label: 'Fonasa por Listado More', icon: TrendingUp, roles: ['auditor'] },
  { path: '/solicitudes', label: 'Solicitudes', icon: ClipboardList, roles: ['auditor'] },
];

const getRoleBadge = (role) => {
  switch(role) {
    case 'auditor': return 'badge-blue';
    case 'supervisor': return 'badge-green';
    default: return 'badge-orange';
  }
};

export default function Layout() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const filteredItems = navItems.filter(item => item.roles.includes(user?.role));

  return (
    <div className="flex h-screen bg-white" data-testid="app-layout">
      {/* Sidebar */}
      <aside className={`${collapsed ? 'w-16' : 'w-60'} flex-shrink-0 macos-sidebar flex flex-col transition-all duration-300`} data-testid="sidebar">
        {/* M Stripe */}
        <div className="m-stripe" />
        {/* Header */}
        <div className="h-14 flex items-center px-4 border-b border-gray-200">
          <button onClick={() => setCollapsed(!collapsed)} className="text-gray-400 hover:text-gray-900 transition-colors" data-testid="toggle-sidebar">
            <Menu size={18} />
          </button>
          {!collapsed && (
            <div className="ml-3 flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-[#00205B] flex items-center justify-center">
                <span className="text-white text-[10px] font-bold">DC</span>
              </div>
              <span className="text-[13px] font-bold text-[#0A0A0A] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>CASMU</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {!collapsed && <div className="sidebar-section-title">Navegacion</div>}
          <div className="px-2 space-y-0.5">
            {filteredItems.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `sidebar-item ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-0' : ''}`
                }
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <item.icon size={18} strokeWidth={1.5} />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* User Section */}
        <div className="p-3 border-t border-gray-200">
          <div className={`flex items-center gap-2.5 p-2 rounded-md bg-gray-100 ${collapsed ? 'justify-center' : ''}`}>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold text-gray-500 truncate">FICHA CASMU</div>
                <span className={`badge ${getRoleBadge(user?.role)}`}>{user?.role}</span>
              </div>
            )}
          </div>
          <button
            onClick={logout}
            className={`mt-2 w-full flex items-center gap-2 text-gray-400 hover:text-[#E32219] px-2.5 py-2 rounded-md hover:bg-[#E32219]/5 transition-all text-[12px] font-medium ${collapsed ? 'justify-center' : ''}`}
            data-testid="logout-btn"
          >
            <LogOut size={16} />
            {!collapsed && <span>Cerrar Sesion</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 overflow-y-auto p-6 md:p-8 bg-[#F9FAFB]" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
